import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
  Box,
} from "@material-ui/core";
import { CustomTheme } from "../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px",
      width: "100%",
      tableLayout: "fixed", // distribute columns evenly
    },
    headerCell: {
      fontWeight: "bold",
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },
    stickyWeekCell: {
      position: "sticky",
      left: 0,
      width: "100px",
      minWidth: "100px",
      backgroundColor: theme.palette.background.default,
      zIndex: 20,
      fontWeight: "bold",
      borderBottom: "none",
    },
    bodyRow: {
      backgroundColor: theme.palette.background.default,
    },
    tableWrapper: {
      overflowX: "hidden", // remove scroll
    },
    yearHeader: {
      textAlign: "center",
      paddingBottom: theme.spacing(1),
      fontWeight: "bold",
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },
  }),
);

interface WeeklyNode {
  card: number;
  cash: number;
  discount: number;
  otherPayments: number;
  saleCount: number;
  saleTotal: number;
  total: number;
  week: string; // Example: "2024 December (Week 52)"
}

interface WeeklySalesTableProps {
  nodes: WeeklyNode[];
}

const ChartSalesTable: React.FC<WeeklySalesTableProps> = ({ nodes }) => {
  const theme: CustomTheme = useTheme();
  const classes = useStyles();

  const getColor = (value: number, list: number[]) => {
    if (value === 0) return theme.palette.background.entity_background;
    const startColor = { r: 222, g: 255, b: 235 };
    const endColor = { r: 2, g: 105, b: 87 };
    const sorted = [...list].sort((a, b) => a - b);

    const index = sorted.indexOf(value);
    const maxIndex = sorted.length - 1;
    const ratio = maxIndex === 0 ? 0 : index / maxIndex;
    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const isDarkColor = (color: string): boolean => {
    let r = 0,
      g = 0,
      b = 0;
    if (color === "#FCFCFC") return false;
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    }
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 128;
  };

  const [chartData, setChartData] = useState<any>({});
  const [valueTotal, setValueTotal] = useState<number[]>([]);
  const [years, setYears] = useState<string[]>([]);

  useEffect(() => {
    if (!nodes || !nodes.length) return;

    const monthYearMap: any = {};
    const totalValues: number[] = [];
    const uniqueYears = new Set<string>();

    nodes.forEach((n) => {
      const match = n.week.match(/(\d{4}) (\w+) \(Week (\d+)\)/);
      if (!match) return;

      const [, year, month, isoWeek] = match;
      const monthYearKey = `${year}-${month}`;

      if (!monthYearMap[monthYearKey]) {
        monthYearMap[monthYearKey] = [];
      }

      monthYearMap[monthYearKey].push({
        isoWeek: Number(isoWeek),
        count: n.saleCount,
        value: n.total,
      });

      if (n.total !== 0) {
        totalValues.push(n.total);
      }
      uniqueYears.add(year);
    });

    const sortedMonthsWithYear = Object.keys(monthYearMap).sort((a, b) => {
      const [yearA, monthA] = a.split("-");
      const [yearB, monthB] = b.split("-");
      const monthOrder = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      if (yearA !== yearB) {
        return parseInt(yearA) - parseInt(yearB);
      }
      return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
    });

    const formattedDataByYear: any = {};
    Array.from(uniqueYears)
      .sort()
      .forEach((year) => {
        const monthsInYear = sortedMonthsWithYear.filter((my) =>
          my.startsWith(year),
        );
        const formatted: any[] = [];
        for (let weekIndex = 0; weekIndex < 5; weekIndex++) {
          const row: any = { week: `Week ${weekIndex + 1}` };
          monthsInYear.forEach((monthYearKey) => {
            const monthWeeks = monthYearMap[monthYearKey].sort(
              (a: any, b: any) => a.isoWeek - b.isoWeek,
            );
            row[monthYearKey] = monthWeeks[weekIndex] || { count: 0, value: 0 };
          });
          formatted.push(row);
        }
        formattedDataByYear[year] = {
          chartData: formatted,
          monthsWithYear: monthsInYear,
        };
      });

    setChartData(formattedDataByYear);
    setValueTotal(totalValues);
    setYears(Array.from(uniqueYears).sort());
  }, [nodes]);

  const getYearMonth = (monthYear: string) => {
    const [year, month] = monthYear.split("-");
    return { year, month };
  };

  return (
    <Box>
      {years.map((year, yearIdx) => {
        const monthsCount = chartData[year]?.monthsWithYear.length || 1;
        const columnWidth = `${100 / monthsCount}%`;

        return (
          <Box key={year} style={{ marginBottom: theme.spacing(4) }}>
            {/* Year Title */}
            <Typography
              variant="h5"
              style={{ margin: "20px 0", fontWeight: "bold" }}
            >
              {year}
            </Typography>

            {/* Table */}
            <Box className={classes.tableWrapper}>
              <Table className={classes.table} size="small">
                {/* Only render header for the first year */}
                {yearIdx === 0 && (
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.stickyWeekCell}>
                        Week
                      </TableCell>
                      {chartData[year]?.monthsWithYear.map(
                        (monthYear: any, idx: any) => (
                          <TableCell
                            key={idx}
                            className={classes.headerCell}
                            align="center"
                            style={{ width: columnWidth }}
                          >
                            <div>{getYearMonth(monthYear).month}</div>
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>
                )}

                <TableBody>
                  {chartData[year]?.chartData.map((weekRow: any, i: any) => (
                    <TableRow key={i} className={classes.bodyRow}>
                      <TableCell className={classes.stickyWeekCell}>
                        {weekRow.week}
                      </TableCell>
                      {chartData[year]?.monthsWithYear.map(
                        (monthYear: any, idx: any) => {
                          const cell = weekRow[monthYear] || {
                            count: 0,
                            value: 0,
                          };
                          const bgColor = getColor(cell.value, valueTotal);
                          return (
                            <TableCell
                              key={idx}
                              align="center"
                              style={{
                                borderBottom: "none",
                                padding: "1px",
                                width: columnWidth,
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: bgColor,
                                  borderRadius: "10px",
                                  height: "42px",
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxSizing: "border-box",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  style={{
                                    fontWeight: "bold",
                                    color: isDarkColor(bgColor)
                                      ? "white"
                                      : "black",
                                  }}
                                >
                                  {cell.value === 0
                                    ? "-"
                                    : cell.value.toFixed(2)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  style={{
                                    color: isDarkColor(bgColor)
                                      ? "white"
                                      : "black",
                                  }}
                                >
                                  {cell.count}
                                </Typography>
                              </div>
                            </TableCell>
                          );
                        },
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ChartSalesTable;
